import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { LogIn, MessageSquare, Sparkles, Shield } from "lucide-react";

interface AuthOverlayProps {
  onClose?: () => void;
}

const AuthOverlay: React.FC<AuthOverlayProps> = ({ onClose }) => {
  const { login, error } = useAuth();

  const handleSignIn = async () => {
    try {
      await login(window.location.pathname);
      // Note: login() will redirect to IdP, so we won't return here
    } catch (err) {
      // Error is handled by auth context
      console.error("Login error:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-md" />

      {/* Auth Card */}
      <div className="relative w-full max-w-md">
        {/* Animated background elements */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-orange-200/30 to-yellow-200/30 dark:from-orange-800/20 dark:to-yellow-800/20 blur-xl animate-pulse" />

        {/* Main card */}
        <div className="relative bg-white/90 dark:bg-neutral-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-neutral-700/30 overflow-hidden">
          {/* Header with gradient */}
          <div className="relative px-8 pt-8 pb-6 bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
              Sign In
            </h1>

            <p className="text-center text-gray-600 dark:text-neutral-400 text-sm">
              Sign in with your organization account to access the AI assistant
            </p>

            {/* Decorative elements */}
            <div className="absolute top-4 right-4">
              <Sparkles className="w-5 h-5 text-orange-400 dark:text-orange-300 animate-pulse" />
            </div>
            <div className="absolute bottom-4 left-4">
              <Shield
                className="w-4 h-4 text-orange-300 dark:text-orange-400 animate-pulse"
                style={{ animationDelay: "1s" }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            {/* Error Message */}
            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl p-4">
                <p className="text-sm text-red-600 dark:text-red-400 text-center">
                  {error}
                </p>
              </div>
            )}

            {/* Sign In Button */}
            <button
              onClick={handleSignIn}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
            >
              <LogIn className="w-5 h-5" />
              <span>Sign In with 3PM</span>
            </button>

            <p className="mt-4 text-center text-xs text-gray-500 dark:text-neutral-400">
              You will be redirected to sign in securely
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthOverlay;
